import {
  ProductSupportFormPayloadUSA_EN,
  ProductSupportValuesKeysWithoutCaptchaUSA_EN,
  ProductSupportFormValuesUSA_EN,
  ProductSupportFormValuesWithoutCaptchaUSA_EN,
} from '@tmh-website/components/shared/StaticForms/ProductSupportFormUSA_EN/ProductSupportFormUSA_EN.contracts';
import type { TFunction } from 'i18next';

import type { ArrayElement } from '@tmh-website/shared/types';
import type { LocalePrefix } from '@tmh-website/shared/const';
import type { SelectOption } from '@tmh-website/ui/FormElements';
import { assertUnreachable } from '@tmh-website/shared/helpers';

import { PRODUCT_SUPPORT_FORM_USA_EN_FIELD_TO_LABEL_TRANSLATION_KEY_MAP } from './ProductSupportFormUSA_EN.config';

type Fields = ProductSupportFormPayloadUSA_EN['fields'];
type Field = ArrayElement<Fields>;

/**
 * Maps the `ProductSupportFormValuesUSA_EN` to `ProductSupportFormPayloadUSA_EN`.
 *
 * @param values - Form values (handled by Formik).
 * @param formParameters - An object compliant with the `ProductSupportFormPayloadUSA_EN['formParameters']` interface.
 * @param locale - The currently-active locale of the page.
 * @param t - The i18n translation function.
 */
export const mapValuesToPayload = (
  values: ProductSupportFormValuesUSA_EN,
  formParameters: ProductSupportFormPayloadUSA_EN['formParameters'],
  locale: LocalePrefix,
  t: TFunction
): ProductSupportFormPayloadUSA_EN => {
  const fields: ProductSupportFormPayloadUSA_EN['fields'] = mapValuesToFields(values, t);

  const { gRecaptchaResponse } = values;

  const pagePath: ProductSupportFormPayloadUSA_EN['pagePath'] =
    typeof window !== 'undefined' ? window.location.pathname : 'unknown';

  return { fields, formParameters, gRecaptchaResponse, locale, pagePath };
};

/**
 * Maps the `ProductSupportFormValuesUSA_EN` to `ProductSupportFormPayloadUSA_EN.fields`.
 *
 * @param values - Form values (handled by Formik).
 * @param t - The i18n translation function.
 */
const mapValuesToFields = (
  values: ProductSupportFormValuesUSA_EN,
  t: TFunction
): ProductSupportFormPayloadUSA_EN['fields'] => {
  const valuesKeys: ProductSupportValuesKeysWithoutCaptchaUSA_EN = Object.keys(values).filter(
    key => key !== 'gRecaptchaResponse'
  ) as ProductSupportValuesKeysWithoutCaptchaUSA_EN;

  return valuesKeys.map(name => ({
    label: t(PRODUCT_SUPPORT_FORM_USA_EN_FIELD_TO_LABEL_TRANSLATION_KEY_MAP[name]),
    name: getFieldName(name),
    value: getFieldValue(name, values, t),
  }));
};

/**
 * Resolves the field's target name based on its original name.
 *
 * @param originalName - The original name/key of the field.
 */
const getFieldName = (originalName: keyof ProductSupportFormValuesWithoutCaptchaUSA_EN): Field['name'] => {
  switch (originalName) {
    case 'email':
    case 'firstName':
    case 'lastName':
    case 'message':
    case 'phone':
    case 'siteName':
      return originalName;

    case 'company':
      return 'companyName';
    case 'customerType':
      return 'background';
    case 'impactedChargers':
      return 'chargerNamesImpacted';
    case 'issueType':
      return 'reason';

    default: {
      return assertUnreachable(originalName);
    }
  }
};

/**
 * Resolves the field value based on its name
 *
 * @param fieldName - The name/key of the field.
 * @param values - The complete set of `SalesFormValues`.
 * @param t - The i18n translation function.
 */
const getFieldValue = (
  fieldName: keyof ProductSupportFormValuesUSA_EN,
  values: ProductSupportFormValuesUSA_EN,
  t: TFunction
): Field['value'] => {
  switch (fieldName) {
    case 'customerType': {
      return resolveOptionLabel(values[fieldName], 'blocks:staticForms.customerTypeOptionsSupport', t);
    }
    case 'issueType': {
      return resolveOptionLabel(values[fieldName], 'blocks:staticForms.issueTypeOptionsSupport', t);
    }
    default: {
      return values[fieldName];
    }
  }
};

/**
 * Resolves the label translation corresponding to the passed-in value.
 *
 * @param optionValue - The value of the option which label we want to resolve.
 * @param allOptionsTranslationKey - The i18n translation key where the option values can be found.
 * @param t - The i18n translation function.
 *
 * @returns - Applicable label translation, or `optionValue`, if the label hasn't been found.
 */
const resolveOptionLabel = (
  optionValue: SelectOption['value'],
  allOptionsTranslationKey: string,
  t: TFunction
): string => {
  const allOptions: SelectOption[] = t(allOptionsTranslationKey, { returnObjects: true }) as SelectOption[];

  const targetOption: SelectOption | undefined = allOptions.find(option => option.value === optionValue);

  return targetOption?.label || `${optionValue}`;
};
